import {
  cloneElement,
  useRef,
  type ComponentProps,
  type JSX,
  type ReactElement,
} from 'react';
import { CSSTransition } from 'react-transition-group';

import { TransitionNodeRefContext } from './useTransitionNodeRef';

export type CSSTransitionWithRefProps = Omit<
  ComponentProps<typeof CSSTransition>,
  'addEndListener'
> & {
  // intended behavior: https://github.com/facebook/react/issues/31824

  // oxlint-disable-next-line typescript/no-explicit-any
  children: ReactElement<any>;
};

export const CSSTransitionWithRef = ({
  children,
  ...props
}: CSSTransitionWithRefProps): JSX.Element => {
  const newRef = useRef<HTMLElement | null>(null);
  const ref = props.nodeRef ?? children.props.ref ?? newRef;
  const isNativeElement = typeof children.type === 'string';

  const childProps = {
    ...children.props,
    ref,
  };

  const {
    onEnter,
    onEntering,
    onEntered,
    onExit,
    onExiting,
    onExited,
    ...restProps
  } = props;

  const onEnterCb = (isAppearing: boolean): void => {
    onEnter?.(ref.current, isAppearing);
  };

  const onEnteringCb = (isAppearing: boolean): void => {
    onEntering?.(ref.current, isAppearing);
  };

  const onEnteredCb = (isAppearing: boolean): void => {
    onEntered?.(ref.current, isAppearing);
  };

  const onExitCb = (): void => {
    onExit?.(ref.current);
  };

  const onExitingCb = (): void => {
    onExiting?.(ref.current);
  };

  const onExitedCb = (): void => {
    onExited?.(ref.current);
  };

  return (
    <CSSTransition
      nodeRef={ref}
      onEnter={onEnterCb}
      onEntering={onEnteringCb}
      onEntered={onEnteredCb}
      onExit={onExitCb}
      onExiting={onExitingCb}
      onExited={onExitedCb}
      addEndListener={() => {}}
      {...restProps}
    >
      <TransitionNodeRefContext.Provider value={ref}>
        {/* oxlint-disable-next-line @eslint-react/no-clone-element */}
        {isNativeElement ? cloneElement(children, childProps) : children}
      </TransitionNodeRefContext.Provider>
    </CSSTransition>
  );
};
